import { useId, useRef, useState, type ReactNode } from 'react'

import { CONSOLE_PRODUCT, MODULES, type ModuleKey } from '@ctcells/core'
import { t } from '@ctcells/i18n'

import { Button } from '../../atoms/Button.tsx'
import { Switch } from '../../atoms/Switch.tsx'
import { Icon } from '../../atoms/Icon.tsx'
import { PasswordRevealButton } from '../../atoms/PasswordInput.tsx'
import { TextInput } from '../../atoms/TextInput.tsx'
import { CtcellsMark } from '../../shell/navIcons.tsx'

import '../screens.css'
import '../resource.css'

/**
 * 로그인 — 와이어프레임 `loginView()` 2883행. 콘솔 · 모듈 패드 공용이고 **제목만 제품명으로 바뀐다**.
 *
 * 🔴 **영문은 제품명에만 쓴다**(§1-1). 나머지 문구는 전부 한글이다.
 * 🔴 계정 잠금 · 인증 실패 문구는 `error_codes.yaml` 기준 한글 메시지다(`error.AUTH_FAILED` ·
 *    `error.ACCOUNT_LOCKED`). **코드 자체를 화면에 쓰지 않는다.**
 * 🔴 **로그인 버튼은 늘 눌린다**(eric 2026-09-23 「포커싱이 안 된 상태에서도 로그인 버튼이
 *    테마색을 유지하면서 클릭 가능하도록 해줘」). 2026-09-23 까지는 React 상태의 두 칸이
 *    다 차야 눌렸는데, **브라우저 자동완성이 그 상태를 안 채운다** — 크롬이 값을 넣어도
 *    `onChange` 가 안 나서 상태는 빈 채로 남고, 사람이 칸을 «건드려야» 그때 버튼이 살아났다.
 *    eric 이 본 「포커싱이 되어야만」이 그것이다.
 *    ⇒ 빈 칸으로 버튼을 막지 않는다. **요청 중일 때만** 비활성이다.
 * 🔴 **두 칸은 «비제어»다.** 제어로 두면 React 가 렌더할 때마다 DOM 값을 제 상태로 되돌려서,
 *    자동완성이 채운 글자가 **아무 재렌더에서 지워진다**(2026-09-23 실측: 「로그인 유지」를
 *    켜는 것만으로 두 칸이 비었다. 로그인 실패 문구가 도착해도 같다). 값을 붙드는 쪽이 DOM 이다.
 * 🔴 **보낼 값은 `FormData` 로 폼에서 읽는다.** 자동완성이 넣었든 사람이 쳤든 같은 자리다.
 * 🔴 정말로 빈 칸이면 **그 칸 아래에 사유 한 줄**이고 서버로 보내지 않는다 — 버튼을 흐리게
 *    두는 대신이다. 사유는 `aria-describedby` 로 그 입력에 묶인다.
 *
 * ⚠ **인증 API 는 붙었다**(2026-09-18 실측 — `POST /auth/login` · `/auth/refresh` 실재).
 *   종전 이 자리의 「백엔드 라우트 0건 · `onSubmit` 자리만 뚫어 뒀다」는 **옛말이다.
 *   되살리지 마라** — 이 화면은 실세션을 태운다.
 * ⚠ 다만 **개발용 계정(`dev-*`)은 비밀번호가 없다** — 시드가 토큰을 컨테이너 안 0600 파일에
 *   쓰고 로그에 남기지 않는다. 그 계정들은 이 화면으로 못 들어온다(CLAUDE.md).
 */
export interface LoginScreenProps {
  /** 모듈 앱이면 그 키. 없으면 통합 콘솔이다 */
  moduleKey?: ModuleKey
  /** 서버가 돌려준 에러 문구. 이미 한글로 온다 */
  errorMessage?: string
  /**
   * 보내는 중. 🔴 **버튼이 비활성이 되는 유일한 까닭이다** — 빈 칸은 이제 버튼을 막지 않는다.
   *    중복 제출을 막는 자리이기도 하다(로그인은 실패 횟수가 계정 잠금으로 이어진다).
   */
  pending?: boolean
  onSubmit?: (credentials: { username: string; password: string; remember: boolean }) => void
}

/**
 * 폼에서 글자 한 칸. ⚠ `FormData.get` 은 `File` 도 돌려줄 수 있는 타입이라
 * `String()` 으로 접으면 `[object Object]` 가 나갈 수 있다 — 글자일 때만 쓴다.
 */
function textOf(data: FormData, key: string): string {
  const raw = data.get(key)
  return typeof raw === 'string' ? raw : ''
}

/** 자동 채움 감지용 애니메이션 이름 — `resource.css` 의 `@keyframes` 와 짝이다 */
const AUTOFILL_ON = 'login-autofill-on'
const AUTOFILL_OFF = 'login-autofill-off'

/** 브라우저 자동 채움 상태인가. ⚠ 모르는 가상 클래스면 jsdom 이 던진다 — 그때는 「아니다」 */
function isAutofilled(input: HTMLInputElement): boolean {
  for (const selector of [':autofill', ':-webkit-autofill']) {
    try {
      if (input.matches(selector)) return true
    } catch {
      // 이 엔진이 모르는 선택자다
    }
  }
  return false
}

/**
 * 떠오르는 라벨 칸 — eric 2026-10-02 「아이디랑 비밀번호가.. 보이다가 적으면.. 위로 올라가면서..
 * 아이디랑 비밀번호 기존 것이 보이는데.. 일단 그렇게 해주고」.
 *
 * 빈 칸이면 칸 이름이 칸 안 가운데 높이에 흐린 글자로 앉아 있고, **초점이 오거나 글자가 있으면**
 * 작아져 칸 안 왼쪽 위로 올라간다. 글자가 있으면 초점이 나가도 위에 머문다.
 *
 * 🔴 **칸 이름은 진짜 `<label for>` 다.** placeholder 로 흉내 내지 않는다 — placeholder 는 치는 순간
 *    사라져 보조기술 이름이 못 된다. 2026-09-28 의 「placeholder + 숨긴 라벨」을 이 라벨 하나가 대신한다.
 * 🔴 **떠오름은 CSS 가 한다**(`resource.css` `.login__float`). 신호는 둘이다:
 *    초점 = `:focus-within`, 글자 있음 = `data-filled`. `data-filled` 는 아래처럼 DOM 에서 읽는다.
 *
 * 🔴 **칸은 비제어다**(위 머리말). 그래서 「글자가 있나」를 React 상태로 받지 못한다 —
 *    DOM 에서 읽는다. 세 갈래로 잡는다:
 *    ⓐ 사람이 친 것 — 감싼 요소가 거품으로 올라오는 `input` 을 듣는다
 *    ⓑ **자동 채움** — 크롬은 `input` 이벤트 없이 값을 넣고, 사람이 페이지를 건드리기 전에는
 *       `value` 조차 빈 문자열로 돌려준다. 대신 `:-webkit-autofill` 이 걸리므로 그 선택자에
 *       빈 애니메이션을 걸어 `animationstart` 로 받는다(`resource.css`). CSS 도 `:has(:autofill)` 로
 *       한 번 더 띄운다 — 라벨이 자동 채움 값과 겹치면 안 된다
 *    ⓒ 첫 렌더 직후 이미 차 있는 경우(브라우저 복원 등) — 자동 채움이 아닌 칸에도 `login-autofill-off`
 *       애니메이션이 마운트 때 한 번 시작되므로 그때 `value` 를 읽는다(effect 로 setState 하지 않는다)
 *
 * 칸 안 오른쪽 「✕」 지우기(eric 2026-09-28 「인풋창에 글자가 채워지면.. X 인풋 오른쪽 부분에 생겨서..
 * 한번에 지울 수 있도록 해줘」)와 비밀번호 칸의 눈 단추(`PasswordRevealButton` — 공용 원자)가 이 틀 안에 선다.
 * 🔴 **✕ 가 맨 오른쪽, 눈은 그 왼쪽**(eric 2026-10-02 「X와 눈 모양.. 아이콘 순서 바꿔줘」) — 두 칸의 ✕ 가
 *    같은 자리에 세로로 맞는다. 탭 순서 = DOM 순서 = 눈에 보이는 순서: 칸 → 눈 → ✕.
 * 🔴 단추는 둘 다 `type="button"` 이다 — 폼 안이라 기본값(submit)이면 누르는 순간 로그인이 나간다.
 * 🔴 칸 폭·높이가 안 변하게 오른쪽 여백을 **늘 비워 둔다**(단추가 설 때 글자가 밀리지 않는다).
 * ⚠ 이 화면 전용이다. 공용 `TextInput` · `Field` 는 고치지 않았다 — 다른 화면 칸은 「라벨이 칸 위」 그대로다.
 */
function FloatField({
  inputId,
  label,
  clearLabel,
  pending,
  error,
  errorId,
  reveal,
  children,
}: {
  inputId: string
  label: string
  clearLabel: string
  pending: boolean
  /** 제출 때 비어 있던 칸의 사유 — 그 입력의 `aria-describedby` 가 `errorId` 로 가리킨다 */
  error?: string | undefined
  errorId: string
  /** 비밀번호 칸이면 눈 단추의 상태 */
  reveal?: { revealed: boolean; onToggle: () => void } | undefined
  children: ReactNode
}) {
  const boxRef = useRef<HTMLSpanElement>(null)
  const [filled, setFilled] = useState(false)

  const inputOf = () => boxRef.current?.querySelector('input') ?? null
  /*
   * 「글자가 있나」의 출처는 이것 하나다 — ✕ · 눈 · 떠오르는 라벨이 전부 이 값을 본다.
   * 🔴 비면 눈 단추가 사라지고 **보임도 가림으로 되돌린다**(eric 2026-10-02 「텍스트가 아무것도 없으면 눈 모양
   *    아이콘 안 보이게 해줘」). 되돌리지 않으면 빈 칸이 `type="text"` 로 남아 다음에 치는 비밀번호가 그대로 보인다.
   */
  const markFilled = (next: boolean) => {
    setFilled(next)
    if (!next && reveal?.revealed === true) reveal.onToggle()
  }
  const sync = () => {
    const input = inputOf()
    markFilled(input !== null && (input.value !== '' || isAutofilled(input)))
  }

  return (
    <div className="login__field">
      <span
        className={reveal === undefined ? 'login__float' : 'login__float login__float--reveal'}
        data-filled={filled ? 'true' : 'false'}
        onAnimationStart={(event) => {
          if (event.animationName === AUTOFILL_ON) markFilled(true)
          else if (event.animationName === AUTOFILL_OFF) sync()
        }}
        onInput={sync}
        ref={boxRef}
      >
        {children}
        <label className="login__label" htmlFor={inputId}>
          {label}
        </label>
        {/* 빈 칸이면 눈 단추를 렌더하지 않는다 — 탭 순서 · 접근성 트리에서도 빠진다 */}
        {reveal === undefined || !filled ? null : (
          <PasswordRevealButton
            className="login__reveal"
            controls={inputId}
            onToggle={reveal.onToggle}
            revealed={reveal.revealed}
          />
        )}
        {!filled ? null : (
          <button
            aria-label={clearLabel}
            className="login__clear"
            onClick={() => {
              if (pending) return
              const input = inputOf()
              if (input === null) return
              input.value = ''
              markFilled(false)
              input.focus()
            }}
            type="button"
            {...(pending
              ? { 'aria-disabled': true, title: t('reason.requestInFlight') }
              : { title: clearLabel })}
          >
            <span aria-hidden="true">✕</span>
          </button>
        )}
      </span>
      {error === undefined ? null : (
        <span className="a-field__error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  )
}

/** 제출 때 비어 있던 칸 */
type MissingFields = { username: boolean; password: boolean }

const NOTHING_MISSING: MissingFields = { username: false, password: false }

export function LoginScreen({
  moduleKey,
  errorMessage,
  pending = false,
  onSubmit,
}: LoginScreenProps) {
  /*
   * 🔴 **계정·비밀번호는 상태로 들지 않는다**(2026-09-23). 「로그인 유지」는 상태여도 된다 —
   *    스위치는 사람이 누르는 것뿐이라 자동완성이 손대지 않는다.
   */
  const [remember, setRemember] = useState(false)
  /* 🔴 **제출을 눌러 봐야 생긴다.** 치는 동안 빨간 줄을 미리 띄우지 않는다(§1-1) */
  const [missing, setMissing] = useState<MissingFields>(NOTHING_MISSING)
  /* 비밀번호 보임/가림 — 화면 상태일 뿐 보낼 값과 무관하다. 제출해도 되돌리지 않는다 */
  const [revealed, setRevealed] = useState(false)
  const usernameId = useId()
  const passwordId = useId()
  const usernameErrId = `${usernameId}-err`
  const passwordErrId = `${passwordId}-err`

  const product = moduleKey === undefined ? CONSOLE_PRODUCT : MODULES[moduleKey].product

  return (
    <div className="login">
      {/* 전체 브랜드 배경 위의 로고. 제품명은 SVG의 접근 이름 하나로 읽는다. */}
      <div className="login__brand">
        {/*
         * 🔴 통합 콘솔은 로고 아래 「CONSOLE」 글자를 두지 않는다(eric 2026-10-29) — 제품명은 그림의
         *    접근 이름이 진다. 모듈 앱은 어느 장비인지가 화면에서 보여야 해서 제품명 글자를 남긴다.
         */}
        <div
          className={
            moduleKey === undefined ? 'login__stack' : 'login__stack login__stack--product'
          }
        >
          <span className="login__lockup">
            <CtcellsMark className="login__mark" label={product} />
            {moduleKey !== undefined && (
              <span aria-hidden="true" className="login__product">
                {product.replace(/^CTCells /, '')}
              </span>
            )}
          </span>
          {/* 고객 사이트의 브랜드 문구(eric 2026-10-29) — 번역하지 않는 원문 · 두 줄 · 둘 다 같은 무게 · 두 줄 다 로고 오른쪽 끝에 맞춘다.
              통합 콘솔은 첫 줄만 머리줄 「CONSOLE」 색(eric 2026-10-29) — 모듈 앱 로그인은 그대로 */}
          <div className="login__tagline">
            <p
              className={
                moduleKey === undefined
                  ? 'login__line login__line--end login__line--accent'
                  : 'login__line login__line--end'
              }
            >
              {t('login.tagline.line1')}
            </p>
            <p className="login__line login__line--end">{t('login.tagline.line2')}</p>
          </div>
        </div>
      </div>
      <div className="login__panel">
        <form
          className="login__box"
          onSubmit={(event) => {
            event.preventDefault()
            /*
             * 🔴 **폼 DOM 에서 읽는다.** 브라우저 자동완성은 값을 넣고도 React `onChange` 를
             *    안 올릴 수 있어서 상태가 빈 채로 남는다 — 그때도 사람 눈에는 채워져 있다.
             *    제어 입력은 그대로 두되(사람이 친 것은 상태로도 흐른다) **보낼 값은 여기서** 뽑는다.
             */
            const data = new FormData(event.currentTarget)
            const typedUsername = textOf(data, 'username').trim()
            const typedPassword = textOf(data, 'password')

            const blanks: MissingFields = {
              username: typedUsername === '',
              password: typedPassword === '',
            }
            setMissing(blanks)
            if (blanks.username || blanks.password) {
              // 첫 빈 칸(화면 순서 — 계정 먼저)으로 포커스를 옮긴다(eric 2026-10-27 「빠진 부분.. 채우라고」)
              document.getElementById(blanks.username ? usernameId : passwordId)?.focus()
              return
            }

            onSubmit?.({ username: typedUsername, password: typedPassword, remember })
          }}
        >
          <h1 className="login__heading">{t('screen.login.title')}</h1>

          {/*
           * 🔴 **폼 전체의 실패다.** 어느 칸이 틀렸는지 서버가 말하지 않으므로 칸을 지목하지 않는다.
           *    `role="alert"` 로 보조기술이 즉시 읽는다.
           */}
          {errorMessage === undefined ? null : (
            <p className="login__fail" role="alert">
              <Icon name="alert" size="md" />
              <span>{errorMessage}</span>
            </p>
          )}

          <FloatField
            clearLabel={t('auth.clearField', { field: t('auth.account') })}
            error={missing.username ? t('validation.login.usernameRequired') : undefined}
            errorId={usernameErrId}
            inputId={usernameId}
            label={t('auth.account')}
            pending={pending}
          >
            <TextInput
              autoComplete="username"
              defaultValue=""
              id={usernameId}
              invalid={missing.username}
              name="username"
              /* 고치기 시작하면 그 칸의 사유는 사라진다 — 고쳐 놓고 붉은 줄이 남아 있으면 안 된다 */
              onChange={() => {
                if (missing.username) setMissing((prev) => ({ ...prev, username: false }))
              }}
              wide
              {...(missing.username ? { describedBy: usernameErrId } : {})}
            />
          </FloatField>
          {/*
          ⚠ 칸에는 `invalid` 를 걸지 않는다 — 서버가 준 것은 「인증 실패」 하나이고
            **비밀번호가 틀렸다고 말한 적이 없다.** 지목은 위 `login__fail` 이 안 한다.
          눈 단추를 누르면 `type` 만 `text` ↔ `password` 로 바뀐다. 칸은 비제어라 값은 DOM 에 그대로 남는다.
        */}
          <FloatField
            clearLabel={t('auth.clearField', { field: t('auth.password') })}
            error={missing.password ? t('validation.login.passwordRequired') : undefined}
            errorId={passwordErrId}
            inputId={passwordId}
            label={t('auth.password')}
            pending={pending}
            reveal={{ revealed, onToggle: () => setRevealed((value) => !value) }}
          >
            <TextInput
              autoComplete="current-password"
              defaultValue=""
              id={passwordId}
              invalid={missing.password}
              name="password"
              onChange={() => {
                if (missing.password) setMissing((prev) => ({ ...prev, password: false }))
              }}
              type={revealed ? 'text' : 'password'}
              wide
              {...(missing.password ? { describedBy: passwordErrId } : {})}
            />
          </FloatField>

          {/*
           * 로그인 유지 — 체크박스 대신 **토글 스위치**(eric 2026-09-22 「로그인 유지를 좀 더 세련되게」).
           * 🔴 **왼쪽 정렬**(eric 2026-09-28 「로그인 유지는 그대로 하되 왼쪽 정렬」) — 스위치가 칸의 왼쪽 선에
           *    붙고 글자가 그 오른쪽이다. 종전의 「글자 왼쪽 · 스위치 오른끝」으로 되돌리지 마라.
           * 스위치가 이름(`aria-label`)을 쥐고 글자는 보조기술에 두 번 읽히지 않게 숨긴다.
           * 글자를 눌러도 켜진다(체크박스의 라벨 클릭과 같은 기대).
           */}
          <div className="login__row">
            <Switch checked={remember} label={t('auth.remember')} onChange={setRemember} />
            <span
              aria-hidden="true"
              className="login__remember"
              onClick={() => setRemember((value) => !value)}
            >
              {t('auth.remember')}
            </span>
          </div>

          {/*
          🔴 **빈 칸으로 막지 않는다**(eric 2026-09-23). 자동완성이 채운 화면에서 버튼이 흐리면
             사람은 「왜 안 눌리지」를 알 길이 없다 — 흐린 버튼의 사유는 툴팁이라 마우스를
             얹어야 보인다. 대신 눌러 보면 **빈 칸 아래에 사유**가 뜬다(위 `FloatField` 의 `error`).
          ⚠ 비활성은 요청 중 한 갈래뿐이다 — 그때는 사유가 「보내는 중」이라 기다리면 풀린다.
        */}
          <Button
            size="md"
            type="submit"
            variant="primary"
            {...(pending
              ? { disabled: true as const, disabledReason: t('reason.requestInFlight') }
              : { disabled: false as const })}
          >
            {t('auth.submit')}
          </Button>
        </form>
      </div>
    </div>
  )
}
